import { useCallback, useEffect, useState } from "react";
import { Plus, Trash, FileText, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ModalShell } from "../components/ModalShell";
import api, { fmtGNF, fmtDate, apiError, logError, openDocument } from "../lib/api";

const STATUS_LABELS = { envoye: "Envoyé", accepte: "Accepté", refuse: "Refusé", expire: "Expiré" };
const STATUS_CLASS = {
  envoye: "badge-partial", accepte: "badge-paid", refuse: "badge-unpaid", expire: "badge-unpaid",
};

function QuoteForm({ onClose, onDone }) {
  const [client, setClient] = useState({ name: "", phone: "" });
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [lines, setLines] = useState([]);
  const [note, setNote] = useState("");
  const [validity, setValidity] = useState("30");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/company").then((r) => setNote(r.data.document_note || "")).catch(() => {});
  }, []);

  useEffect(() => {
    if (search.length < 1) { setResults([]); return undefined; }
    const t = setTimeout(() => api.get(`/products?search=${encodeURIComponent(search)}`)
      .then((r) => setResults(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [search]);

  const total = lines.reduce((a, l) => a + (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0), 0);
  const upd = (uid, k, v) => setLines(lines.map((l) => (l.uid === uid ? { ...l, [k]: v } : l)));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/quotes", {
        client_name: client.name || null, client_phone: client.phone || null,
        items: lines.map((l) => ({ product_id: l.product_id, name: l.name, qty: parseFloat(l.qty), unit_price: parseFloat(l.unit_price) })),
        note, validity_days: parseInt(validity, 10) || 30,
      });
      toast.success("Devis créé — aucun effet sur le stock ni les finances");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <ModalShell size="max-w-lg" testid="quote-modal" onClose={onClose}>
      <h3 className="font-display font-bold text-lg">Nouveau devis</h3>
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="quote-client-name" className="kinput" placeholder="Nom du client" value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} />
        <input data-testid="quote-client-phone" className="kinput" placeholder="Téléphone (optionnel)" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} />
      </div>
      <div className="relative">
        <input data-testid="quote-product-search" className="kinput" placeholder="Rechercher un produit (ou laisser vide pour une prestation)" value={search} onChange={(e) => setSearch(e.target.value)} />
        {results.length > 0 && (
          <div className="mt-1 border border-[#E9E4DA] rounded-xl p-1 max-h-40 overflow-y-auto bg-white">
            {results.map((p) => (
              <button type="button" key={p.id} data-testid="quote-product-result"
                onClick={() => { setLines([...lines, { uid: crypto.randomUUID(), product_id: p.id, name: p.name, qty: 1, unit_price: p.sale_price }]); setSearch(""); setResults([]); }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-[#F6F4EF] text-sm">{p.name} · {fmtGNF(p.sale_price)}</button>
            ))}
          </div>
        )}
      </div>
      <button type="button" data-testid="quote-add-free-line" className="btn-ghost py-2 text-xs w-full"
        onClick={() => setLines([...lines, { uid: crypto.randomUUID(), product_id: null, name: search || "Prestation", qty: 1, unit_price: 0 }])}>
        + Ajouter une ligne libre (prestation)
      </button>
      {lines.map((l) => (
        <div key={l.uid} data-testid="quote-line" className="bg-[#F6F4EF]/70 rounded-2xl p-3 space-y-2">
          <div className="flex items-center gap-2">
            <input data-testid="quote-line-name" className="kinput flex-1" value={l.name} onChange={(e) => upd(l.uid, "name", e.target.value)} />
            <button type="button" data-testid="quote-line-remove" className="text-[#B4432F]" onClick={() => setLines(lines.filter((x) => x.uid !== l.uid))}><Trash size={16} /></button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="quote-line-qty" type="number" className="kinput" value={l.qty} onChange={(e) => upd(l.uid, "qty", e.target.value)} />
            <input data-testid="quote-line-price" type="number" className="kinput" value={l.unit_price} onChange={(e) => upd(l.uid, "unit_price", e.target.value)} />
          </div>
        </div>
      ))}
      <div>
        <label className="text-xs text-gray-500">Validité (jours)</label>
        <input data-testid="quote-validity-input" type="number" className="kinput" value={validity} onChange={(e) => setValidity(e.target.value)} />
      </div>
      <textarea data-testid="quote-note-input" className="kinput" rows={3} placeholder="Note (coordonnées bancaires, conditions…) — la note par défaut s'applique si vide" value={note} onChange={(e) => setNote(e.target.value)} />
      <p data-testid="quote-total" className="font-display font-bold text-lg">Total : {fmtGNF(total)}</p>
      <div className="flex gap-2">
        <button className="btn-ghost flex-1 py-2.5" onClick={onClose}>Annuler</button>
        <button data-testid="quote-submit-btn" className="btn-amber flex-1 py-2.5" disabled={busy || lines.length === 0} onClick={submit}>Créer le devis</button>
      </div>
    </ModalShell>
  );
}

export default function Quotes() {
  const [quotes, setQuotes] = useState([]);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(() => api.get("/quotes").then((r) => setQuotes(r.data)).catch((e) => logError(e)), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (q, status) => {
    try {
      await api.patch(`/quotes/${q.id}/status`, { status });
      toast.success(`Devis ${q.number} : ${STATUS_LABELS[status]}`); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const convert = async (q) => {
    try {
      const { data } = await api.post(`/quotes/${q.id}/convert`);
      toast.success(`Vente ${data.sale.number} créée depuis le devis ${q.number}`); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openQuotePdf = async (q) => {
    try {
      await openDocument("quote", q.id);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="quotes-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-display font-bold text-2xl">Devis</h1>
          <p className="text-xs text-gray-500">Un devis n'a aucun effet sur le stock, les finances ni les statistiques.</p>
        </div>
        <button data-testid="add-quote-btn" onClick={() => setShowAdd(true)} className="btn-amber py-2 px-4 flex items-center gap-1.5 text-sm"><Plus size={16} weight="bold" /> Devis</button>
      </div>

      <div className="space-y-2.5">
        {quotes.map((q) => (
          <div key={q.id} data-testid="quote-row" className="kcard p-4">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="font-mono-doc text-sm font-semibold">{q.number}</span>
              <span className="text-xs text-gray-400">{fmtDate(q.created_at)}</span>
              <span className="flex-1" />
              <span className="font-display font-bold tabular-nums">{fmtGNF(q.total)}</span>
              <span data-testid="quote-status" className={`${STATUS_CLASS[q.status]} px-2.5 py-1 rounded-full text-xs font-semibold`}>{STATUS_LABELS[q.status]}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1.5">
              {q.client_name || "Client non précisé"} · {q.items?.map((i) => `${i.name} ×${i.qty}`).join(", ")}
              <span className="text-gray-400"> · valable jusqu'au {String(q.valid_until).slice(0, 10)}</span>
            </p>
            {q.sale_number && <p className="text-xs text-[#2E7D5B] mt-1">Transformé en vente {q.sale_number}</p>}
            <div className="flex flex-wrap gap-2 mt-3">
              <button data-testid="quote-pdf-btn" onClick={() => openQuotePdf(q)} className="btn-ghost text-xs py-1.5 px-3 flex items-center gap-1"><FileText size={14} /> PDF</button>
              {!q.sale_id && (
                <>
                  <button data-testid="quote-convert-btn" onClick={() => convert(q)} className="btn-ghost text-xs py-1.5 px-3 flex items-center gap-1 text-[#2E7D5B]"><ArrowRight size={14} /> Transformer en vente</button>
                  <button data-testid="quote-refuse-btn" onClick={() => setStatus(q, "refuse")} className="btn-ghost text-xs py-1.5 px-3 text-[#B4432F]">Refusé</button>
                </>
              )}
            </div>
          </div>
        ))}
        {quotes.length === 0 && <div className="kcard p-8 text-center text-sm text-gray-400">Aucun devis pour l'instant.</div>}
      </div>

      {showAdd && <QuoteForm onClose={() => setShowAdd(false)} onDone={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}
